"use client";

import { forwardRef, useEffect, useRef, type TextareaHTMLAttributes } from "react";

import { cn } from "@/src/lib/utils";
import { Text } from "@/src/components/ui/Typography";
import { textVariants } from "@/src/components/ui/Typography/typographyVariants";

interface TextareaFieldProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: string;
  error?: string;
  hint?: string;
}

const FLOAT_CLASSES =
  "peer-focus:top-0 peer-focus:scale-75 peer-focus:text-neutral-500 " +
  "peer-[:not(:placeholder-shown)]:top-0 peer-[:not(:placeholder-shown)]:scale-75 peer-[:not(:placeholder-shown)]:text-neutral-500";

// Same floating-label field as TextField, but grows with its content
// instead of scrolling internally.
export const TextareaField = forwardRef<HTMLTextAreaElement, TextareaFieldProps>(
  function TextareaField(
    { label, required, error, hint, id, name, className, value, ...props },
    forwardedRef,
  ) {
    const innerRef = useRef<HTMLTextAreaElement>(null);

    useEffect(() => {
      const node = innerRef.current;
      if (!node) return;
      node.style.height = "auto";
      node.style.height = `${node.scrollHeight}px`;
    }, [value]);

    const textareaId = id ?? name;

    return (
      <div className="flex flex-col">
        {/*
          The textarea's height is JS-driven (auto-grow effect below), so a
          plain `absolute bottom-0` overlay — anchored to this wrapper's own
          box — drifts below the textarea's actual bottom edge whenever that
          height changes, showing as a stray second line under the real
          border. `grid` + `[grid-area:1/1]` on both the textarea and the
          overlay puts them in the exact same cell, so the overlay always
          tracks the textarea's real (dynamic) height instead of the
          wrapper's.
        */}
        <div className="relative grid">
          <textarea
            ref={(node) => {
              innerRef.current = node;
              if (typeof forwardedRef === "function") forwardedRef(node);
              else if (forwardedRef) forwardedRef.current = node;
            }}
            id={textareaId}
            name={name}
            required={required}
            placeholder=" "
            value={value}
            rows={1}
            className={cn(
              textVariants({ size: "bodyMD" }),
              "peer w-full [grid-area:1/1] resize-none overflow-hidden border-b bg-transparent pt-6 pb-2 text-neutral-700 outline-none transition-colors focus:border-neutral-700 font-medium",
              error ? "border-error" : "border-neutral-300",
              className,
            )}
            {...props}
          />

          {/* Hover line: sweeps left-to-right over the static border below,
              same target color as the old instant hover swap — matches the
              Link button's underline animation. peer-focus:scale-x-0 keeps
              it from fighting the textarea's own darker focus border when
              both states are active at once. */}
          <span
            aria-hidden
            className={cn(
              "pointer-events-none w-full [grid-area:1/1] self-end h-px origin-left scale-x-0 transition-transform duration-400 ease-out peer-hover:scale-x-100 peer-focus:scale-x-0",
              error ? "bg-error" : "bg-neutral-500",
            )}
          />

          <label
            htmlFor={textareaId}
            className={cn(
              "pointer-events-none absolute top-6 left-0 origin-left scale-100 text-neutral-400 transition-all duration-200",
              FLOAT_CLASSES,
            )}
          >
            <Text as="span" size="bodyMD" className="text-inherit font-light">
              {label}
              {required && <span className="text-brand ml-1">*</span>}
            </Text>
          </label>
        </div>

        {(error || hint) && (
          <Text
            size="labelSM"
            className={cn("mt-1.5", error ? "text-error" : "text-neutral-500")}
          >
            {error || hint}
          </Text>
        )}
      </div>
    );
  },
);
